import React, { useMemo } from 'react';
import { Row, Show, Spinner,Column } from '.'
import { classNameBuilder } from '@/helpers';
import translations from '@/translations';
import { Size } from '@/types/themeType';


export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'flat-primary'
  | 'flat-dark'
  | 'flat-light'
type ButtonVariant = 'solid' | 'outline'
type ButtonSize = Size | '2xl'

export type ButtonProps = {
  label: string
  icon?: React.ReactNode
  color?: ButtonColor
  variant?: ButtonVariant
  pending?: boolean
  disabled?: boolean
  size?: ButtonSize
  type?: 'submit' | 'button'
  onClick?: React.ButtonHTMLAttributes<HTMLButtonElement>['onClick']
  grow?: boolean
  className?: string
}

interface TranslatedButtonProps {
  translationKey: string
  icon?: React.ReactNode
  color?: ButtonColor
  variant?: ButtonVariant
  pending?: boolean
  disabled?: boolean
  size?: ButtonSize
  type?: 'submit' | 'button'
  onClick?: React.ButtonHTMLAttributes<HTMLButtonElement>['onClick']
  grow?: boolean
  className?: string
  values?: (string | number)[]
}

export const ButtonComponent: React.FC<ButtonProps> = ({
  label,
  icon,
  color = 'primary',
  variant = 'solid',
  pending,
  disabled,
  size = '2xl',
  type,
  onClick,
  grow,
  className
}) => {
  return (
    <button
      tabIndex={pending || disabled ? -1 : undefined}
      type={type}
      disabled={disabled}
      onClick={(event) => {
        if (pending) {
          return
        }
        onClick?.(event)
      }}
      className={classNameBuilder(
        'group/button relative overflow-hidden font-bold uppercase outline-none focus-visible:ring-4 cursor-pointer',
        transitionClasses,
        grow ? 'flex-1' : '',
        sizeClassMap[size],
        'rounded-full',
        variantClassMap[variant][color] || '',
        !pending && !disabled
          ? buttonEnabledVariantClassMap[variant][color]
          : '',
        pending ? pendingClasses : '',
        disabled ? disabledClasses : '',
        disabled ? variantDisabledClassMap[variant][color] || '' : '',
        className
      )}
    >
      <Show when={pending}>
        <Column className="absolute inset-0 items-center justify-center">
          <Spinner
            className={['text-current', spinnerSizeMap[size]].join(' ')}
          />
        </Column>
      </Show>
      <Row
        className={classNameBuilder(
          'h-full w-full items-center justify-center gap-xs',
          transitionClasses,
          sizeClassMap[size],
          // variant === 'solid' && color === 'primary'
          //   ? 'bg-black bg-opacity-0 group-hover/button:bg-opacity-30 group-active/button:bg-opacity-50'
          //   : '',
          paddingClassMap[size],
          pending ? 'invisible' : ''
        )}
      >
        <Row className="shrink-0 items-center gap-xs">
          <Show when={!!icon}>{icon}</Show>
          {label}
        </Row>
      </Row>
    </button>
  )
}

const Translated: React.FC<TranslatedButtonProps> = ({
  translationKey,
  icon,
  color = 'primary',
  variant = 'solid',
  pending,
  disabled,
  size = 'xl',
  type,
  onClick,
  grow,
  className,
  values
}) => {
  const label = useMemo(() => {
    // @ts-expect-error no types available for translations
    const translatedLabel = translations[translationKey]

    if (translatedLabel) {
      return values?.length
        ? translations.formatString(translatedLabel, ...values)
        : translatedLabel
    }

    return translationKey
  }, [translationKey, values])

  return (
    <Button
      label={label}
      icon={icon}
      color={color}
      variant={variant}
      pending={pending}
      disabled={disabled}
      size={size}
      type={type}
      onClick={onClick}
      grow={grow}
      className={className}
    />
  )
}

interface ButtonWithTranslated extends React.FC<ButtonProps> {
  Translated: React.FC<TranslatedButtonProps>
}

export const Button = ButtonComponent as ButtonWithTranslated
Button.Translated = Translated

export default Button

// ... existing code ...

const sizeClassMap: Record<ButtonSize, string> = {
  xs: 'min-h-[1.875rem] text-[10px]',
  sm: 'min-h-[2.25rem] text-sm',
  md: 'min-h-[2.5rem] text-sm',
  lg: 'min-h-[3rem] text-base',
  xl: 'min-h-[3.5rem] text-base',
  '2xl': 'min-h-[4rem] text-base'
}
const paddingClassMap: Record<ButtonSize, string> = {
  xs: 'px-2.5',
  sm: 'px-3',
  md: 'px-8',
  lg: 'px-8',
  xl: 'px-10',
  '2xl': 'px-10'
}

const buttonEnabledVariantClassMap: Record<
  ButtonVariant,
  Record<ButtonColor, string>
> = {
  solid: {
    primary: 'focus:ring-electric-indigo-300',
    secondary: 'hover:bg-gray-50 active:bg-gray-100 focus:ring-gray-500',
    tertiary:
      'hover:bg-gray-50 active:bg-gray-100 focus:ring-electric-indigo-900/50',
    quaternary:
      'hover:bg-[#06B6D4] active:bg-[#06B6D4] focus:ring-electric-indigo-900/50',
    'flat-primary':
      'hover:bg-electric-indigo-800 active:bg-primary-800 focus:ring-electric-indigo-100',
    'flat-dark':
      'hover:bg-electric-indigo-600 active:bg-primary-800 focus:ring-electric-indigo-100',
    'flat-light':
      'hover:bg-electric-indigo-100 active:bg-electric-indigo-200 focus:ring-electric-indigo-100'
  },
  outline: {
    primary:
      'hover:bg-primary-100 active:bg-primary-300 focus:ring-primary-300',
    secondary: 'hover:bg-white/20 active:bg-white/40 focus:ring-secondary-300',
    tertiary: 'hover:bg-white active:bg-white/40 focus:ring-secondary-300',
    quaternary: 'hover:bg-white active:bg-white/40 focus:ring-secondary-300',
    'flat-primary':
      'hover:bg-electric-indigo-800 active:bg-primary-800 focus:ring-electric-indigo-100',
    'flat-dark':
      'hover:bg-electric-indigo-600 active:bg-primary-800 focus:ring-electric-indigo-100',
    'flat-light':
      'hover:bg-electric-indigo-100 active:bg-electric-indigo-200 focus:ring-electric-indigo-100'
  }
}

const variantClassMap: Record<ButtonVariant, Record<ButtonColor, string>> = {
  solid: {
    primary:
      'bg-gradient-to-r from-[#280166] from-[2%] to-[#8B4AF4] to-[103%] text-white',
    secondary: 'bg-white border border-electric-indigo-900/50 text-gray-600',
    tertiary:
      'bg-white border border-electric-indigo-900/50 text-electric-indigo-900',
    quaternary: 'bg-[#09D4E1] text-electric-indigo-900',
    'flat-dark': 'bg-electric-indigo-500 text-white',
    'flat-primary': 'bg-electric-indigo-800 text-white',
    'flat-light': 'bg-white text-electric-indigo-900'
  },
  outline: {
    primary: 'border border-[#280166] text-[#333333]',
    secondary: 'border border-white/50 text-white',
    tertiary: 'border border-electric-indigo-500 text-electric-indigo-500',
    quaternary: 'border border-electric-indigo-900 text-electric-indigo-900',
    'flat-dark': 'bg-electric-indigo-500 text-white',
    'flat-primary': 'bg-electric-indigo-800 text-white',
    'flat-light': 'bg-white text-electric-indigo-900'
  }
}

const variantDisabledClassMap: Record<
  ButtonVariant,
  Record<ButtonColor, string>
> = {
  solid: {
    primary: 'disabled:bg-primary-200',
    secondary: 'disabled:bg-secondary-200/50',
    tertiary: 'disabled:bg-white/50',
    quaternary: 'disabled:bg-gray-100',
    'flat-dark': '',
    'flat-primary': '',
    'flat-light': 'disabled:opacity-50'
  },
  outline: {
    primary: 'disabled:bg-white disabled:text-primary-500',
    secondary: 'disabled:bg-white disabled:text-secondary-500',
    tertiary: 'disabled:border-white/40 disabled:text-white/40',
    quaternary: 'disabled:border-white/40 disabled:text-white/40',
    'flat-dark': '',
    'flat-primary': '',
    'flat-light': 'disabled:opacity-50'
  }
}

const transitionClasses = 'transition duration-300'
const pendingClasses = 'cursor-progress'
const disabledClasses =
  'disabled:text-opacity-50 disabled:shadow-none disabled:cursor-not-allowed'

const spinnerSizeMap: Record<ButtonSize, string> = {
  xs: 'w-4 h-4',
  sm: 'w-5 h-5',
  md: 'w-6 h-6',
  lg: 'w-7 h-7',
  xl: 'w-8 h-8',
  '2xl': 'w-8 h-8'
}
